import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, CameraOff, FilterX, Pencil, Plus, Printer, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAssetTypes } from '../../api/queries';
import type { AssetStatus } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { AssetLink } from '../../components/assets/AssetLink';
import { EmployeeSelect, LocationSelect } from '../../components/pickers';
import { Button, ButtonLink, Card, Checkbox, EmptyState, ErrorBox, Input, Loading, PageHeader, Pagination, Select, StatusBadge, Table, Td, Th, Tr } from '../../components/ui';
import { formatDate, STATUS_LABELS, STATUS_ORDER } from '../../lib/format';

type SortKey = 'asset_number' | 'name' | 'manufacturer' | 'model' | 'status' | 'purchase_date' | 'created_at' | 'updated_at';
const PAGE_SIZE = 50;

export function AssetListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const types = useAssetTypes();

  const get = (k: string) => params.get(k) ?? '';
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const sort = (get('sort') || 'asset_number') as SortKey;
  const order = get('order') === 'desc' ? 'desc' : 'asc';
  const status = get('status');

  const qParam = get('q');
  const [term, setTerm] = useState(qParam);
  useEffect(() => setTerm(qParam), [qParam]);

  const update = (changes: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.delete('page');
    setParams(next, { replace: true });
  };

  const query = {
    q: get('q') || undefined,
    status: status || (get('show_disposed') ? undefined : 'active,in_stock,repair,retired,lost,unknown'),
    type: get('type') || undefined,
    employee: get('employee') || undefined,
    location: get('location') || undefined,
    unassigned: (get('unassigned') || undefined) as 'true' | 'false' | undefined,
    top_level: (get('top_level') || undefined) as 'true' | 'false' | undefined,
    photo_missing: (get('photo_missing') || undefined) as 'true' | 'false' | undefined,
    sort,
    order: order as 'asc' | 'desc',
    page,
    page_size: PAGE_SIZE,
  };

  const list = useQuery({
    queryKey: ['assets', 'list', query],
    queryFn: () => unwrap(api.GET('/api/assets', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (nr: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(nr)) n.delete(nr);
      else n.add(nr);
      return n;
    });

  const hasFilters = ['q', 'status', 'type', 'employee', 'location', 'unassigned', 'top_level', 'photo_missing', 'show_disposed'].some((k) => params.has(k));

  const SortHeader = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <Th>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-slate-900"
        onClick={() => update({ sort: k, order: sort === k && order === 'asc' ? 'desc' : 'asc' }, false)}
      >
        {children}
        {sort === k && (order === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </Th>
  );

  const rows = list.data?.data ?? [];
  const allOnPage = rows.length > 0 && rows.every((a) => selected.has(a.asset_number));

  return (
    <>
      <PageHeader
        title="Assets"
        subtitle={list.data ? `${list.data.total} Treffer` : undefined}
        actions={
          <>
            {selected.size > 0 && (
              <Button icon={<Printer className="size-4" />} onClick={() => window.open(`/labels?assets=${[...selected].join(',')}`, '_blank')}>
                {selected.size} Etikett{selected.size === 1 ? '' : 'en'} drucken
              </Button>
            )}
            {selected.size > 1 && can('asset:write') && (
              <Button icon={<Pencil className="size-4" />} onClick={() => navigate(`/assets/bulk?assets=${[...selected].join(',')}`)}>
                {selected.size} gemeinsam bearbeiten
              </Button>
            )}
            {can('asset:write') && (
              <ButtonLink to="/assets/new" variant="primary" icon={<Plus className="size-4" />}>
                Asset anlegen
              </ButtonLink>
            )}
          </>
        }
      />

      <Card className="mb-4 p-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-6">
          <form
            className="relative lg:col-span-2"
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: term.trim() || null });
            }}
          >
            <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-slate-400" />
            <Input className="pl-8" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Suche: Nr., Seriennr., Name, Hostname …" aria-label="Suche" />
          </form>
          <Select value={status} onChange={(e) => update({ status: e.target.value || null })} aria-label="Status">
            <option value="">Status: alle</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s as AssetStatus]}
              </option>
            ))}
          </Select>
          <Select value={get('type')} onChange={(e) => update({ type: e.target.value || null })} aria-label="Typ">
            <option value="">Typ: alle</option>
            {types.data?.map((t) => (
              <option key={t.id} value={t.key}>
                {t.label}
              </option>
            ))}
          </Select>
          <EmployeeSelect value={get('employee')} onChange={(v) => update({ employee: v || null })} emptyLabel="Mitarbeiter: alle" includeInactive />
          <LocationSelect value={get('location')} onChange={(v) => update({ location: v || null })} emptyLabel="Standort: alle" includeInactive />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
          <Checkbox label="Nur Hauptgeräte (nicht eingebaut)" checked={get('top_level') === 'true'} onChange={(e) => update({ top_level: e.target.checked ? 'true' : null })} />
          <Checkbox label="Ohne Mitarbeiter" checked={get('unassigned') === 'true'} onChange={(e) => update({ unassigned: e.target.checked ? 'true' : null })} />
          <Checkbox label="Foto fehlt" checked={get('photo_missing') === 'true'} onChange={(e) => update({ photo_missing: e.target.checked ? 'true' : null })} />
          {!status && (
            <Checkbox label="Verschrottete einbeziehen" checked={!!get('show_disposed')} onChange={(e) => update({ show_disposed: e.target.checked ? '1' : null })} />
          )}
          {hasFilters && (
            <Button size="sm" variant="ghost" icon={<FilterX className="size-4" />} onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </Card>

      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox error={list.error} className="m-4" />
        ) : rows.length === 0 ? (
          <EmptyState title="Keine Assets gefunden">{hasFilters ? 'Filter anpassen oder zurücksetzen.' : null}</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Alle auf dieser Seite auswählen"
                      checked={allOnPage}
                      onChange={() =>
                        setSelected((s) => {
                          const n = new Set(s);
                          rows.forEach((a) => (allOnPage ? n.delete(a.asset_number) : n.add(a.asset_number)));
                          return n;
                        })
                      }
                    />
                  </Th>
                  <SortHeader k="asset_number">Nr.</SortHeader>
                  <SortHeader k="name">Bezeichnung</SortHeader>
                  <Th>Typ</Th>
                  <SortHeader k="status">Status</SortHeader>
                  <Th>Mitarbeiter</Th>
                  <Th>Standort</Th>
                  <SortHeader k="purchase_date">Kaufdatum</SortHeader>
                </tr>
              </thead>
              <tbody className={list.isPlaceholderData ? 'opacity-60' : undefined}>
                {rows.map((a) => (
                  <Tr key={a.id} onClick={() => navigate(`/assets/${a.asset_number}`)}>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`${a.asset_number} auswählen`} checked={selected.has(a.asset_number)} onChange={() => toggle(a.asset_number)} />
                    </Td>
                    <Td className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <AssetLink asset={a} />
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5 font-medium text-slate-900">
                        {a.name}
                        {a.photo_missing && (
                          <span title="Foto fehlt" aria-label="Foto fehlt" className="inline-flex text-amber-500">
                            <CameraOff className="size-3.5" />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {[a.manufacturer, a.model].filter(Boolean).join(' ')}
                        {a.serial_number ? ` · SN ${a.serial_number}` : ''}
                        {a.parent ? ` · eingebaut in ${a.parent.asset_number}` : ''}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap">{a.asset_type.label}</Td>
                    <Td>
                      <StatusBadge status={a.status} />
                    </Td>
                    <Td className="whitespace-nowrap">
                      {a.effective_employee ? (
                        <span className={a.employee ? undefined : 'text-slate-400'} title={a.employee ? undefined : 'über übergeordnetes Gerät'}>
                          {a.effective_employee.short_code}
                        </span>
                      ) : (
                        '–'
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {a.effective_location ? <span className={a.location ? undefined : 'text-slate-400'}>{a.effective_location.name}</span> : '–'}
                    </Td>
                    <Td className="whitespace-nowrap">{formatDate(a.purchase_date)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPage={(p) => update({ page: String(p) }, false)} />
          </>
        )}
      </Card>
    </>
  );
}
